import React, { useState } from "react";
import { Badge, Button, Form } from "react-bootstrap";
import api from "../../../services/api";
import { formatDateTimeDMY } from "../../../utils/dateFormat";

// The same Google review link the website's documents carry.
export const GOOGLE_REVIEW_LINK = "https://share.google/SoSE0qDIehhvaL411";
export const INSTAGRAM_LINK = "https://www.instagram.com/indianodyssey.in/";

/** Sent, or not asked on purpose: what a trip needs before it can be completed. */
export function reviewDecided(booking) {
  return Boolean(booking?.reviewRequestSentAt || booking?.reviewNotAsked);
}

function reviewStatus(booking) {
  if (booking?.reviewNotAsked) return { label: "Not asking", variant: "secondary" };
  if (booking?.cashbackGoogleReviewReceived) return { label: "Google review received", variant: "success" };
  if (booking?.reviewRequestSentAt) return { label: "Review requested", variant: "primary" };
  if (["ONGOING", "COMPLETED"].includes(booking?.tripStatus)) return { label: "Review pending", variant: "danger" };
  return { label: "Ask after the trip", variant: "light" };
}

export function reviewFilterValues(booking) {
  const values = [reviewStatus(booking).label];
  if (booking?.cashbackReviewVideoReceived) values.push("Review video received");
  if (booking?.cashbackTravelPhotosReceived) values.push("Travel photos received");
  return values;
}

function tripName(booking) {
  return booking.tripTitle ? `${booking.tripTitle} trip` : "trip";
}

function customerName(booking) {
  return booking.userName || "Customer";
}

/**
 * The messages the Review column copies, in the order they are usually sent: the main request
 * with the cashback offer right after the trip, the video and photos follow-up for the free
 * travel reel, and a short Google reminder for anyone who has not replied.
 */
export const REVIEW_MESSAGES = [
  {
    key: "cashback",
    label: "Review + Cashback Offer",
    build: (booking) => [
      `Dear ${customerName(booking)} 👋`,
      "",
      `Thank you for travelling with Indian Odyssey Trips! We hope you enjoyed your ${tripName(booking)}.`,
      "",
      "Share your trip experience with us and get cashback:",
      "",
      "⭐ Google Review + 4 Trip Photos + Instagram Follow",
      "💰 ₹100 cashback per person",
      "",
      "🎥 Google Review + 4 Trip Photos + Instagram Follow + Review Video",
      "💰 ₹150 cashback per person",
      "",
      "🌟 Google Review + 4 Trip Photos + Instagram Follow + Review Video + Instagram Collaboration (100+ followers)",
      "💰 ₹200 cashback per person",
      "",
      "👥 Cashback is for each person who does it from their own account. The Google review, Instagram follow and Instagram collaboration are counted person by person - only those who complete them receive the cashback.",
      "For example, if 3 people in your group each post a Google review and follow us on Instagram, all 3 receive it.",
      "",
      "🔗 Google review:",
      GOOGLE_REVIEW_LINK,
      "",
      "📸 Follow us on Instagram:",
      INSTAGRAM_LINK,
      "",
      "Once done, please send us the names of everyone who took part (as they appear on Google and Instagram) along with your PhonePe number. We will verify and transfer the cashback right away.",
      "",
      "Thank you for your valuable support ❤️",
      "Indian Odyssey Trips",
    ],
  },
  {
    key: "video",
    label: "Video & Photos (Free Reel)",
    build: (booking) => [
      `Hello ${customerName(booking)},`,
      "",
      "If you are recording a review video, please send it to us once it is ready - we'll take care of the editing. 🎥",
      "",
      "Please also share a few pictures of your group and videos from the trip. We'll turn them into a beautiful travel reel for you, completely free. 🎬",
      "",
      "And if you haven't yet, we'd really appreciate a Google review:",
      `🔗 ${GOOGLE_REVIEW_LINK}`,
      "",
      "Thank you!",
      "Indian Odyssey Trips",
    ],
  },
  {
    key: "google",
    label: "Google Review Reminder",
    build: (booking) => [
      `Dear ${customerName(booking)},`,
      "",
      `Thank you for travelling with Indian Odyssey Trips! We hope you enjoyed your ${tripName(booking)}.`,
      "",
      "Could you spare a minute to share your experience on Google? Your review helps other travellers plan their trips with us.",
      "",
      `⭐ ${GOOGLE_REVIEW_LINK}`,
      "",
      "Thank you!",
      "Indian Odyssey Trips",
    ],
  },
];

export function reviewRequestMessage(booking, key = "cashback") {
  const message = REVIEW_MESSAGES.find((item) => item.key === key) || REVIEW_MESSAGES[0];
  return message.build(booking).join("\n");
}

/**
 * The Review column: copy the request, tick it sent - or decide not to ask - and tick off what the
 * customer sends back. Saved straight away, outside revisions.
 */
export default function BookingReviewCell({ booking, onPatched }) {
  const [busy, setBusy] = useState(false);
  const status = reviewStatus(booking);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/api/admin/bookings/${booking.id}/review`, patch);
      onPatched(data);
    } catch (error) {
      alert(error?.response?.data?.message || "Could not update the review.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (message) => {
    try {
      await navigator.clipboard.writeText(reviewRequestMessage(booking, message.key));
      alert(booking.reviewRequestSentAt
        ? `"${message.label}" copied. Send it on WhatsApp.`
        : `"${message.label}" copied. Send it on WhatsApp, then tick Review request sent.`);
    } catch (error) {
      alert("Could not copy the message.");
    }
  };

  const toggleNotAsked = (checked) => {
    if (!checked) {
      save({ notAsked: false });
      return;
    }
    const reason = window.prompt("Why not ask this customer for a review? (optional)", "");
    if (reason === null) return;
    save({ notAsked: true, notAskedReason: reason });
  };

  return (
    <div className="d-flex flex-column align-items-start gap-1 small" style={{ minWidth: 200 }}>
      <Badge bg={status.variant} text={status.variant === "light" ? "dark" : undefined} className={status.variant === "light" ? "border" : ""}>
        {status.label}
      </Badge>
      <div className="text-muted mt-1">Copy for WhatsApp</div>
      <div className="d-flex flex-column gap-1 w-100">
        {REVIEW_MESSAGES.map((message) => (
          <Button
            key={message.key}
            size="sm"
            variant="outline-success"
            className="text-start py-0"
            onClick={() => copy(message)}
            disabled={booking.reviewNotAsked}
          >
            <i className="bi bi-whatsapp me-1" aria-hidden="true" />{message.label}
          </Button>
        ))}
      </div>
      <Form.Check
        type="checkbox"
        id={`booking-${booking.id}-review-sent`}
        label="Review request sent"
        checked={Boolean(booking.reviewRequestSentAt)}
        disabled={busy}
        onChange={(event) => save({ requestSent: event.target.checked })}
      />
      {booking.reviewRequestSentAt && (
        <div className="text-muted">Sent on {formatDateTimeDMY(booking.reviewRequestSentAt, "-")}</div>
      )}
      <Form.Check
        type="checkbox"
        id={`booking-${booking.id}-review-not-asked`}
        label="Do not ask for review"
        checked={Boolean(booking.reviewNotAsked)}
        disabled={busy}
        onChange={(event) => toggleNotAsked(event.target.checked)}
      />
      {booking.reviewNotAsked && booking.reviewNotAskedReason && (
        <div className="text-muted text-wrap">Reason: {booking.reviewNotAskedReason}</div>
      )}
      <div className="border-top pt-1 mt-1 w-100">
        <div className="text-muted">Received</div>
        <Form.Check
          type="checkbox"
          id={`booking-${booking.id}-google-review`}
          label="Google review"
          checked={Boolean(booking.cashbackGoogleReviewReceived)}
          disabled={busy}
          onChange={(event) => save({ googleReviewReceived: event.target.checked })}
        />
        <Form.Check
          type="checkbox"
          id={`booking-${booking.id}-review-video`}
          label="Review video"
          checked={Boolean(booking.cashbackReviewVideoReceived)}
          disabled={busy}
          onChange={(event) => save({ reviewVideoReceived: event.target.checked })}
        />
        <Form.Check
          type="checkbox"
          id={`booking-${booking.id}-travel-photos`}
          label="Travel photos"
          checked={Boolean(booking.cashbackTravelPhotosReceived)}
          disabled={busy}
          onChange={(event) => save({ travelPhotosReceived: event.target.checked })}
        />
      </div>
      {booking.cashbackReviewRequestNote && (
        <div className="text-muted text-wrap">Note: {booking.cashbackReviewRequestNote}</div>
      )}
    </div>
  );
}
